'use client';

import { ChevronDown } from 'lucide-react';
import { Popover } from 'radix-ui';
import { useState } from 'react';
import { cn, focusRing } from './cn';
import {
  AppDot,
  AppMark,
  CROW_APPS,
  type CrowApp,
  type CrowAppId,
  crowApp,
  type RecentItem,
  useEcosystem,
  useRecents,
} from './ecosystem';
import { popoverPanelClass } from './popover';

/** "il y a 5 min", "hier", "12 oct." */
export function relativeTime(at: string, now = Date.now()): string {
  const minutes = Math.round((now - Date.parse(at)) / 60_000);
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  if (minutes < 48 * 60) return 'hier';
  return new Date(at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function Recent({ item }: { item: RecentItem }) {
  return (
    <a
      href={item.url}
      className={cn(
        'flex min-w-0 items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-crow-raised',
        focusRing,
      )}
    >
      <AppDot app={item.app} />
      <span className="min-w-0 flex-1 truncate text-crow-compact">{item.title}</span>
      <span className="shrink-0 text-crow-label text-crow-text-3">
        {[item.kind, relativeTime(item.at)].filter(Boolean).join(' · ')}
      </span>
    </a>
  );
}

/**
 * The product switcher every Crowlingo app shows (the mockup, brand/crow-ecosysteme.html): the
 * current app's colour square, its name and a chevron open the other applications. Nothing else
 * opens them: the grid icon stays Crowdex's "All apps". The apps listed are those the app's server
 * names (`useEcosystem`: its deployment's, else every app of its Crowlingo environment); with none,
 * the grid shows the current app alone, without a word.
 */
export function ProductSwitcher({
  current,
  compact = false,
  onOpen,
  organization,
  className,
}: {
  current: CrowAppId;
  /** The small form, for a sidebar's head (Crowdex). */
  compact?: boolean;
  /**
   * Opens an app; return true when handled (Crowdex opens it in a tab). By default the browser goes
   * to its address.
   */
  onOpen?: (app: CrowApp, url: string) => boolean;
  /** The organization's name, in the grid's head. */
  organization?: string | null;
  className?: string;
}) {
  const ecosystem = useEcosystem();
  const [opened, setOpened] = useState(false);
  const recents = useRecents(current, opened);
  const self = crowApp(current);
  const apps = CROW_APPS.filter((app) => app.id === current || ecosystem.has(app.id));
  const open = (app: CrowApp) => {
    const url = ecosystem.get(app.id);
    if (!url || app.id === current) return;
    if (onOpen?.(app, url)) return;
    window.location.assign(url);
  };
  return (
    <Popover.Root onOpenChange={(open) => open && setOpened(true)}>
      <Popover.Trigger
        title="Changer d’application"
        className={cn(
          'inline-flex shrink-0 cursor-pointer items-center rounded-lg transition-colors duration-[var(--crow-dur-1)] hover:bg-crow-accent-tint data-[state=open]:bg-crow-accent-tint',
          compact ? 'h-[30px] gap-[7px] pr-1.5 pl-1' : 'h-[38px] gap-2.5 pr-2.5 pl-1.5',
          focusRing,
          className,
        )}
      >
        <AppMark app={current} size={compact ? 20 : 26} />
        <b
          className={cn(
            'font-crow-heading font-bold text-crow-text',
            compact ? 'text-crow-body' : 'text-crow-title',
          )}
        >
          {self?.name ?? current}
        </b>
        <ChevronDown className={cn('text-crow-text-3', compact ? 'size-3' : 'size-3.5')} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(popoverPanelClass, 'w-[400px] max-w-[calc(100vw-16px)] p-2.5')}
        >
          <div className="flex items-center px-1 pb-2.5">
            <b className="text-crow-compact font-semibold">Applications Crowlingo</b>
            {organization ? (
              <span className="ml-auto truncate pl-3 text-crow-label text-crow-text-3">
                Organisation : {organization}
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-3 gap-1">
            {apps.map((app) => (
              <Popover.Close asChild key={app.id}>
                <button
                  type="button"
                  aria-current={app.id === current ? 'page' : undefined}
                  onClick={() => open(app)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-[10px] px-1.5 pt-3.5 pb-2.5 text-center hover:bg-crow-raised',
                    app.id === current && 'bg-crow-accent-tint hover:bg-crow-accent-tint',
                    focusRing,
                  )}
                >
                  <AppMark app={app.id} size={40} />
                  <b className="mt-1 text-crow-compact font-semibold">{app.name}</b>
                  <span className="text-crow-label text-crow-text-3">{app.description}</span>
                </button>
              </Popover.Close>
            ))}
          </div>
          {recents && recents.length > 0 ? (
            <>
              <div className="my-2 h-px bg-crow-line" />
              <p className="px-1 pb-1 text-crow-label text-crow-text-3">
                Récents dans les autres apps
              </p>
              {recents.slice(0, 5).map((item) => (
                <Recent key={`${item.app}:${item.url}`} item={item} />
              ))}
            </>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
